<template>
  <UIModal
    :is-open="!!shareData"
    :title="t('shareModal.title')"
    @close="closeShareModal"
  >
    <div v-if="shareData" class="space-y-2">
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
        {{ shareData.title }}
      </p>

      <button
        type="button"
        class="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 text-left transition-colors"
        @click="copyLink"
      >
        <span
          class="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-600"
        >
          <UIIcon name="library" class="w-5 h-5 text-gray-600 dark:text-gray-300" />
        </span>
        <div class="flex-1 min-w-0">
          <span class="font-medium text-gray-900 dark:text-gray-100">
            {{ t('shareModal.copyLink') }}
          </span>
          <p class="text-sm text-gray-500 dark:text-gray-400 truncate">
            {{ shareData.url }}
          </p>
        </div>
      </button>

      <button
        type="button"
        class="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 text-left transition-colors"
        @click="copyLinkWithMessage"
      >
        <span
          class="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-600"
        >
          <UIIcon name="list" class="w-5 h-5 text-gray-600 dark:text-gray-300" />
        </span>
        <div class="flex-1 min-w-0">
          <span class="font-medium text-gray-900 dark:text-gray-100">
            {{ t('shareModal.copyLinkWithMessage') }}
          </span>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            {{ t('shareModal.copyLinkWithMessageHint') }}
          </p>
        </div>
      </button>

      <button
        v-if="canUseNativeShare"
        type="button"
        class="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 text-left transition-colors"
        @click="nativeShare"
      >
        <span
          class="flex items-center justify-center w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30"
        >
          <UIIcon name="share" class="w-5 h-5 text-primary-600 dark:text-primary-400" />
        </span>
        <div class="flex-1 min-w-0">
          <span class="font-medium text-gray-900 dark:text-gray-100">
            {{ t('shareModal.shareVia') }}
          </span>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            {{ t('shareModal.shareViaHint') }}
          </p>
        </div>
      </button>
    </div>
  </UIModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import UIModal from '@/components/ui/UIModal.vue'
import UIIcon from '@/components/ui/UIIcon.vue'
import { useShareModal } from '@/composables/useShareModal'
import { toast } from '@/utils/toast'
import { createLogger } from '@/utils/logger'

const fileLogger = createLogger('ShareModal')
const { t } = useI18n()
const { shareData, closeShareModal } = useShareModal()

const canUseNativeShare = computed(() => typeof navigator !== 'undefined' && !!navigator.share)

const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (err) {
    fileLogger.error('Failed to copy to clipboard', err)
    return false
  }
}

const copyLink = async () => {
  if (!shareData.value) return
  const ok = await copyToClipboard(shareData.value.url)
  if (ok) {
    toast.success({
      title: t('shareModal.linkCopied'),
      message: t('shareModal.linkCopiedMessage')
    })
    closeShareModal()
  } else {
    toast.error({
      title: t('player.shareError'),
      message: t('player.shareErrorMessage')
    })
  }
}

const copyLinkWithMessage = async () => {
  if (!shareData.value) return
  const text = [shareData.value.title, shareData.value.text, shareData.value.url]
    .filter(Boolean)
    .join('\n\n')
  const ok = await copyToClipboard(text)
  if (ok) {
    toast.success({
      title: t('shareModal.linkCopied'),
      message: t('shareModal.linkCopiedMessage')
    })
    closeShareModal()
  } else {
    toast.error({
      title: t('player.shareError'),
      message: t('player.shareErrorMessage')
    })
  }
}

const nativeShare = async () => {
  if (!shareData.value || !navigator.share) return
  try {
    await navigator.share({
      title: shareData.value.title,
      text: shareData.value.text,
      url: shareData.value.url
    })
    toast.success({
      title: t('shareModal.shared'),
      message: t('shareModal.sharedMessage')
    })
    closeShareModal()
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') {
      return
    }
    fileLogger.error('Native share failed', err)
    toast.error({
      title: t('player.shareError'),
      message: t('player.shareErrorMessage')
    })
  }
}
</script>
